import { durableNetworkFallback } from "../durableNetworkFallback";
import type { DataSession } from "../../../data/core/session";
import type { z } from "zod";
import { NoAuthError } from "@utils/NoAuthError";
import { RouterOutputs, trpc } from "@utils/trpc";
import { getQueryKey } from "@trpc/react-query";
import { libraryListSchema } from "@libs/wire";
import { EmptyServerResponseError } from "@utils/durableCache";

export type LibraryQueryData = RouterOutputs["user"]["library"]["listItems"];

/**
 * Compile-time drift check, bidirectional: `z.infer<typeof
 * libraryListSchema>` and `LibraryQueryData` must be mutually
 * assignable. The forward direction catches "schema is missing fields
 * the router has"; the backward direction catches "schema is narrower
 * than the router output" (which would cause runtime parsing to reject
 * valid server data and pin the cache permanently to stale rows).
 *
 * tRPC's `RouterOutputs[...]` is a compile-time-only TypeScript type, so
 * we can't extract a zod schema from it directly. This assertion is the
 * runtime-cheap equivalent: one schema, one type, drift caught early.
 */
type _LibraryWireSchemaSubset =
  z.infer<typeof libraryListSchema> extends LibraryQueryData ? true : never;
type _LibraryWireSchemaSuperset =
  LibraryQueryData extends z.infer<typeof libraryListSchema> ? true : never;
const _libraryWireSchemaDriftCheck: [
  _LibraryWireSchemaSubset,
  _LibraryWireSchemaSuperset,
] = [true, true];
void _libraryWireSchemaDriftCheck;

/**
 * Bare tRPC queryKey — no uid prefix.
 *
 * The uid prefix was originally added defensively for "multi-account
 * safety," but lissen-app is single-user: logout and account switches end
 * the data session and delete the durable cache. Worse, the
 * uid prefix introduced a cold-start race: any caller computing the key
 * from `useMe()`'s async `userData?.id` saw `""` during the gap before
 * useMe resolved, writing an orphan SQLite row. Once useMe landed, the
 * new uid-prefixed key had no row → `initialData` returned undefined →
 * the instant-render guarantee was lost.
 */
export const libraryQueryKey = () => getQueryKey(trpc.user.library.listItems);

export const libraryQuery = (data: DataSession) =>
  data.cache.durableQueryOptions({
    fallbackOnError: durableNetworkFallback,
    queryKey: libraryQueryKey(),
    queryFn: () => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("libraryQuery queryFn");
      }
      return data.api.user.library.listItems.query();
    },
    schema: libraryListSchema,
    // Library invariant: a logged-in user's library is never legitimately
    // empty (we always seed at least `discover_daily` + `liked_<uid>` on
    // signup). Reject an empty response rather than persist it.
    validate: (data) => {
      if (!data.length) {
        throw new EmptyServerResponseError(libraryQueryKey());
      }
    },
  });
